import React, { useRef } from 'react';
import { Paperclip, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { TaskyIconButton } from '../button/TaskyIconButton';

export interface TaskyFileUploadProps {
  label?: string;
  files?: File[];
  uploadProgress?: number; // 0-100
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  onFilesChange?: (files: File[]) => void;
  onRemove?: (file: File, index: number) => void;
}

export const TaskyFileUpload: React.FC<TaskyFileUploadProps> = ({
  label = 'Attach Files',
  files = [],
  uploadProgress,
  accept,
  multiple = true,
  disabled,
  className,
  onFilesChange,
  onRemove,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleClick = () => inputRef.current?.click();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(e.target.files ?? []);
    onFilesChange?.(next);
  };

  return (
    <div className={cn('space-y-3', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <div className="flex items-center gap-2">
        <TaskyIconButton aria-label={label} type="button" onClick={handleClick} disabled={disabled}>
          <Paperclip />
        </TaskyIconButton>
        <span className="text-sm text-gray-600">{label}</span>
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="relative h-20 w-full rounded-tasky-md bg-gray-100 flex items-center justify-center text-xs text-gray-500 overflow-hidden"
            >
              <span className="truncate px-2">{f.name}</span>
              {onRemove && (
                <button
                  type="button"
                  className="absolute top-1 right-1 rounded-full bg-white/80 hover:bg-white p-0.5 text-gray-700"
                  onClick={() => onRemove(f, i)}
                  aria-label="Remove file"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {typeof uploadProgress === 'number' && uploadProgress >= 0 && (
        <div>
          <div className="bg-gray-200 h-2 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${Math.min(100, Math.max(0, uploadProgress))}%` }}
            />
          </div>
          <div className="text-xs text-gray-600 mt-1.5">
            Uploading: {Math.round(uploadProgress)}%
          </div>
        </div>
      )}
    </div>
  );
};
